import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import {
  sendVerificationEmail,
  hasCurrentUserErrors,
  getCurrentUserFirstListing,
  fetchCurrentUserFirstListing,
  fetchCurrentUserNotifications,
} from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';
import { usePresencePing } from '../../hooks/usePresencePing';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/** Order / sale detail routes use TransactionPage; skip global notification poll there (page polls chat itself). */
const isTransactionDetailPath = pathname => {
  if (!pathname || typeof pathname !== 'string') {
    return false;
  }
  const pathOnly = pathname.split('?')[0].replace(/\/$/, '') || '/';
  return /^\/(order|sale)\/[^/]+$/.test(pathOnly);
};

/** InboxPage runs its own list + notification poll; avoid duplicating API calls from the topbar. */
const isInboxPath = pathname => {
  if (!pathname || typeof pathname !== 'string') {
    return false;
  }
  const pathOnly = pathname.split('?')[0].replace(/\/$/, '') || '/';
  return /^\/inbox\/(orders|sales)$/.test(pathOnly);
};

const shouldSkipGlobalNotificationPoll = pathname =>
  isTransactionDetailPath(pathname) || isInboxPath(pathname);

const GLOBAL_NOTIFICATIONS_POLL_MS = 15000;

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @param {number} props.notificationCount number of notifications
 * @param {Function} props.onLogout logout function
 * @param {Function} props.onManageDisableScrolling manage disable scrolling function
 * @param {Function} props.onResendVerificationEmail resend verification email function
 * @param {Object} props.sendVerificationEmailInProgress send verification email in progress
 * @param {Object} props.sendVerificationEmailError send verification email error
 * @param {boolean} props.hasGenericError has generic error
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const config = useConfiguration();
  const {
    notificationCount = 0,
    bookingsNotificationCount = 0,
    messagesNotificationCount = 0,
    hasGenericError,
    isAuthenticated,
    currentUser,
    onFetchFirstListing,
    onPollNotifications,
    location,
    ...rest
  } = props;

  const roles = getCurrentUserTypeRoles(config, currentUser);
  const isProvider = !!(isAuthenticated && currentUser && roles?.provider);

  // Fetch first listing as soon as container mounts when user is logged in (e.g. on account pages).
  // This runs before the loadable Topbar may have resolved, so the link is correct when the menu renders.
  useEffect(() => {
    if (isAuthenticated && onFetchFirstListing) {
      onFetchFirstListing();
    }
  }, [isAuthenticated, onFetchFirstListing]);

  // Global presence pinger: keeps the provider's `lastSeenOnline` metadata fresh
  // (every 30s + on tab focus) so clients can render the "Online" badge on their
  // listing cards. Only providers post listings and can appear as experts.
  usePresencePing(isProvider);

  // Light polling for inbox / booking dots when not on transaction chat (TransactionPage polls messages itself).
  useEffect(() => {
    if (!isAuthenticated || !onPollNotifications) {
      return undefined;
    }
    if (shouldSkipGlobalNotificationPoll(location?.pathname)) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (shouldSkipGlobalNotificationPoll(window.location.pathname)) {
        return;
      }
      onPollNotifications();
    }, GLOBAL_NOTIFICATIONS_POLL_MS);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, location?.pathname, onPollNotifications]);

  return (
    <Topbar
      location={location}
      notificationCount={notificationCount}
      bookingsNotificationCount={bookingsNotificationCount}
      messagesNotificationCount={messagesNotificationCount}
      showGenericError={hasGenericError}
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      {...rest}
    />
  );
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserSaleNotificationCount = 0,
    currentUserOrderNotificationCount = 0,
    currentUserSaleMessageCount = 0,
    currentUserOrderMessageCount = 0, 
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  const firstListing = getCurrentUserFirstListing(state);
  const bookingsNotificationCount =
    currentUserSaleNotificationCount + currentUserOrderNotificationCount;
  const messagesNotificationCount = currentUserSaleMessageCount + currentUserOrderMessageCount;
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    firstListing,
    bookingsNotificationCount,
    messagesNotificationCount,
    notificationCount:
      bookingsNotificationCount + messagesNotificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onFetchFirstListing: () => dispatch(fetchCurrentUserFirstListing()),
  onPollNotifications: () => dispatch(fetchCurrentUserNotifications()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
